import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Badge, Alert, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faPlus, 
  faEdit, 
  faTrash, 
  faEye,
  faSearch,
  faArrowLeft,
  faUsers,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { companyService } from '../../services/companyService';
import { userService } from '../../services/userService';

const CompaniesList = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesData, usersData] = await Promise.all([
          companyService.getAllCompanies(),
          userService.getAllUsers()
        ]);
        setCompanies(companiesData);
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch companies data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      try {
        await companyService.deleteCompany(companyId);
        setCompanies(companies.filter(company => company.id !== companyId));
      } catch (error) {
        setError('Failed to delete company');
        console.error('Error deleting company:', error);
      }
    }
  };

  const getUsersCountForCompany = (companyId) => {
    return users.filter(user => user.companyId === companyId).length;
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="secondary">Inactive</Badge>;
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || company.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">All Companies</h2>
          <p className="text-muted mb-0">Manage property management companies</p>
        </div>
        <div className="d-flex gap-2">
          <Button as="a" href="/admin/companies/onboard" variant="primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add New Company
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faBuilding} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{companies.length}</h4>
                  <p className="text-muted mb-0">Total Companies</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faUsers} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.filter(u => u.role === 'manager').length}</h4>
                  <p className="text-muted mb-0">Total Managers</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faBuilding} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{companies.filter(c => c.status === 'active').length}</h4>
                  <p className="text-muted mb-0">Active Companies</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{companies.filter(c => {
                    const onboardingDate = c.onboardingDate?.toDate?.();
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    return onboardingDate && onboardingDate > oneWeekAgo;
                  }).length}</h4>
                  <p className="text-muted mb-0">New This Week</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search companies by name or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="text-muted">
                Showing {filteredCompanies.length} of {companies.length} companies
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Companies Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">Companies List</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Company Name</th>
                <th>Location</th>
                <th>Contact Person</th>
                <th>Managers</th>
                <th>Status</th>
                <th>Onboarding Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => (
                <tr key={company.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
                      <div>
                        <div className="fw-bold">{company.name}</div>
                        <small className="text-muted">{company.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>{company.location}</td>
                  <td>
                    <div>
                      <div>{company.contactPerson}</div>
                      <small className="text-muted">{company.contactPhone}</small>
                    </div>
                  </td>
                  <td>
                    <Badge bg="info">
                      {getUsersCountForCompany(company.id)} managers
                    </Badge>
                  </td>
                  <td>{getStatusBadge(company.status)}</td>
                  <td>
                    {company.onboardingDate ? 
                      company.onboardingDate.toDate().toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/admin/companies/${company.id}`)}
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate(`/admin/companies/${company.id}/edit`)}
                        title="Edit Company"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteCompany(company.id)}
                        title="Delete Company"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {filteredCompanies.length === 0 && (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faBuilding} className="text-muted mb-3" size="3x" />
              <h5 className="text-muted">No companies found</h5>
              <p className="text-muted">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Get started by adding your first company'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <Button as="a" href="/admin/companies/onboard" variant="primary">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add First Company
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CompaniesList; 